import { useContext, FC, PropsWithChildren, useState, createContext } from 'react';
import noop from 'utils/noop';
import { MovableInkBlock } from '../types';
import { useMovableInkBlocksMutation } from '../hooks/useMovableInkBlocksMutation';

export type MovableInkBlockContextState = {
  blockPagesTotal: number;
  blocks: MovableInkBlock[];
  cachedBlocks: MovableInkBlock[];
  isFetchingBlocks: boolean;
  hasBlocksError: boolean;
  selectedBlock?: MovableInkBlock;
  setSelectedBlock: (block: MovableInkBlock) => void;
  refetchBlocks: ({
    tokenId,
    campaignId,
    search,
    companyId,
  }: { tokenId: number; campaignId?: number; search?: string; companyId?: number }) => void;
  setCachedBlocks: (
    blocks: MovableInkBlock[] | ((prev: MovableInkBlock[]) => MovableInkBlock[]),
  ) => void;
  hasBlocks: boolean;
};

export const defaultState = {
  blockPagesTotal: 0,
  blocks: [],
  cachedBlocks: [],
  isFetchingBlocks: false,
  hasBlocksError: false,
  setSelectedBlock: noop,
  refetchBlocks: noop,
  setCachedBlocks: noop,
  hasBlocks: false,
};

export const MovableInkBlockContext = createContext<MovableInkBlockContextState>(defaultState);

export const useMovableInkBlockContext = () => useContext(MovableInkBlockContext);

export const MovableInkBlockContextProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const [selectedBlock, setSelectedBlock] = useState<MovableInkBlock>();
  const [cachedBlocks, setCachedBlocks] = useState<MovableInkBlock[]>([]);

  const {
    blocks = [],
    isLoading: isFetchingBlocks,
    totalPages: blockPagesTotal,
    refetchBlocks,
    isError: hasBlocksError,
  } = useMovableInkBlocksMutation();

  const hasBlocks = !isFetchingBlocks && blocks.length > 0;

  const value = {
    blockPagesTotal,
    blocks,
    cachedBlocks,
    isFetchingBlocks,
    selectedBlock,
    hasBlocksError,
    setSelectedBlock,
    refetchBlocks,
    setCachedBlocks,
    hasBlocks,
  };

  return (
    <MovableInkBlockContext.Provider value={value}>{children}</MovableInkBlockContext.Provider>
  );
};
